import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { LenticMark, DemoFooter } from '../../components/demo/DemoChrome';
import { DEMO_LIBRARY } from '../../data/demo';
import { assessmentUrlFor } from '../../data/demoInstruments';
export function Intake() {
  const {
    postingUrl,
    setPostingUrl,
    postingText,
    setPostingText,
    useSamplePosting,
    useLegalSamplePosting,
    candidateCount,
    setCandidateCount,
    setMode,
    bundle
  } = useDemo();
  const [pasteMode, setPasteMode] = useState<'url' | 'text'>('url');
  const [collapsing, setCollapsing] = useState(false);
  const navigate = useNavigate();
  const filled = pasteMode === 'url' ? postingUrl.trim().length > 0 : postingText.trim().length > 0;
  const submit = () => {
    if (!filled || collapsing) return;
    setMode(candidateCount === 1 ? 'single' : 'cohort');
    // The field collapses upward into the source chip that opens the next screen.
    setCollapsing(true);
    window.setTimeout(() => navigate('/demo/analysis'), 220);
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-741" data-mp="16ph7e5qxpk1m">
      <div className="px-8 pt-8" data-id="element-742" data-mp="1bn0n7a3a64vo">
        <LenticMark data-id="element-743" data-mp="1blxvx121q0f8" />
      </div>

      <main className="flex flex-1 justify-center px-8 py-20" data-id="element-744" data-mp="1bl66k7z1kyxe">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="w-full max-w-[640px]" data-id="element-745" data-mp="e0tepp3uvse1">
          <p className="eyebrow text-brand" data-id="element-746" data-mp="nwd741j7o8y1">New assessment</p>
          <h1 className="mt-4 text-[42px] font-semibold leading-[1.1] tracking-tight text-ink" data-id="element-747" data-mp="spg64p6rcalm">
            Start from the role.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-muted" data-id="element-748" data-mp="3rcalkmoeo326">
            Paste the posting. Lentic builds the assessment from its gated scenario library — and
            shows you every check it passes on the way.
          </p>

          <motion.div initial={false} animate={{
          height: collapsing ? 0 : 'auto',
          opacity: collapsing ? 0 : 1,
          y: collapsing ? -12 : 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-12 overflow-hidden" data-id="element-749" data-mp="3kbqb3yo1u4ck">
            <label htmlFor={pasteMode === 'url' ? 'posting-url' : 'posting-text'} className="eyebrow block text-muted" data-id="element-750" data-mp="35e7op2yug38a">
              Posting
            </label>
            {pasteMode === 'url' ? <input id="posting-url" value={postingUrl} onChange={e => setPostingUrl(e.target.value)} placeholder="https://careers.example.com/financial-controller" className="mt-3 w-full border-b border-rule bg-transparent pb-3 font-mono-num text-[14px] text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-751" data-mp="3adag3klwiaxs" /> : <textarea id="posting-text" rows={7} value={postingText} onChange={e => setPostingText(e.target.value)} placeholder="Paste the full text of the posting." className="mt-3 w-full rounded-sm border border-rule bg-white p-4 text-[14px] leading-relaxed text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-752" data-mp="2cl2sm0xtt4z1" />}

            <div className="mt-3 flex items-center justify-between gap-4 text-[12px]" data-id="element-753" data-mp="2hj4bakrcb29z">
              <button type="button" onClick={() => setPasteMode(pasteMode === 'url' ? 'text' : 'url')} className="text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink" data-id="element-754" data-mp="2mhfkqudq3aro">
                {pasteMode === 'url' ? 'paste the text instead' : 'use a URL instead'}
              </button>
              <button type="button" onClick={() => {
              setPasteMode('url');
              useSamplePosting();
            }} className="font-medium text-brand underline decoration-brand/40 underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-755" data-mp="1lckqap10ihpw">
                use a sample posting
              </button>
            </div>
            <button type="button" onClick={() => {
            setPasteMode('url');
            useLegalSamplePosting();
          }} className="mt-2 font-mono-num text-[11px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink hover:decoration-brand" data-id="element-756" data-mp="252d12wbopauc">
              or a trainee solicitor posting → LAW-001
            </button>
          </motion.div>

          <div className="mt-14" data-id="element-757" data-mp="2vbvgybn0fngw">
            <p className="eyebrow text-muted" data-id="element-758" data-mp="2vcwucmri0m1s">Candidates</p>
            <div className="mt-3 flex items-center gap-5" data-id="element-759" data-mp="309dlzqxwpijm">
              <button type="button" aria-label="Fewer candidates" onClick={() => setCandidateCount(Math.max(1, candidateCount - 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-760" data-mp="30c00ybn4n0bv">
                <MinusIcon className="h-4 w-4" data-id="element-761" data-mp="3j7q4426sq2rc" />
              </button>
              <span className="font-mono-num text-[40px] font-medium leading-none text-ink" data-id="element-762" data-mp="3nz7yg7ebd53c">
                {candidateCount}
              </span>
              <button type="button" aria-label="More candidates" onClick={() => setCandidateCount(Math.min(12, candidateCount + 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-763" data-mp="3sy181oqhcby8">
                <PlusIcon className="h-4 w-4" data-id="element-764" data-mp="1xwh3qmwcg064" />
              </button>
            </div>
            <div className="relative mt-4 min-h-[42px] max-w-md" data-id="element-765" data-mp="2bnm7a02u1clq">
              <AnimatePresence mode="wait" initial={false} data-id="element-766" data-mp="2boxanysd8icb">
                <motion.p key={candidateCount === 1 ? 'single' : 'cohort'} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="text-[13.5px] leading-relaxed text-muted" data-id="element-767" data-mp="26vkmtqvigfkm">
                  {candidateCount === 1 ? 'One candidate — a tailored deep probe. Results won’t be comparable to anyone else’s, by design.' : `${candidateCount} candidates — one identical instrument for all. That’s what makes the results comparable, and the comparison defensible.`}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          <div className="mt-14 flex justify-end" data-id="element-768" data-mp="3bu7qd2wehomo">
            <button type="button" onClick={submit} disabled={!filled} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-muted/25 disabled:text-muted" data-id="element-769" data-mp="3bx3p2tg2ohc6">
              Analyse the role
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-770" data-mp="2f59yptjy66tq" />
            </button>
          </div>

          <p className="eyebrow mt-14 normal-case tracking-[0.06em] text-muted/70" data-id="element-771" data-mp="2j8odkgw5gh5l">
            FIN · LAW library · {DEMO_LIBRARY} · last gated against 3 frontier models
          </p>
          <a href={assessmentUrlFor(bundle.instrument)} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[13px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-772" data-mp="hep4m26i2bw2">
            Open the live assessment ↗
          </a>
        </motion.div>
      </main>

      <DemoFooter data-id="element-773" data-mp="2mltyehl0mzij" />
    </div>;
}